export const TUTOR_GRID_SIZE = 12;

const STOP = new Set([
    "THAT", "THIS", "WITH", "FROM", "HAVE", "WERE", "THEY", "THEM", "THEN", "THAN",
    "WHAT", "WHEN", "YOUR", "WILL", "BEEN", "INTO", "SOME", "MORE", "ALSO", "JUST",
    "OVER", "ONLY", "SUCH", "MOST", "OTHER", "THEIR", "THERE", "THESE", "THOSE",
    "ABOUT", "WHICH", "WOULD", "COULD", "SHOULD", "BEING", "AFTER", "BEFORE",
]);

function cleanWord(raw) {
    return String(raw || "").toUpperCase().replace(/[^A-Z]/g, "");
}

function uniqueClues(rows) {
    const seen = new Set();
    const out = [];
    for (const row of rows) {
        const word = cleanWord(row.word);
        if (word.length < 4 || word.length > TUTOR_GRID_SIZE || STOP.has(word) || seen.has(word)) {
            continue;
        }
        seen.add(word);
        const meaning = String(row.meaning || "").trim();
        out.push({
            word,
            meaning,
            fromTutorDef: Boolean(row.fromTutorDef) && Boolean(meaning),
        });
    }
    return out;
}

/** Pull WORD — meaning lines, or unique content words from the pasted story. */
export function extractTutorClues(sourceText, maxWords = 12) {
    const text = String(sourceText || "").trim();
    if (!text) {
        return [];
    }
    const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const fromDefs = [];
    for (const line of lines) {
        const match = line.match(/^([A-Za-z][A-Za-z'-]{2,})\s*[—–\-:=]\s+(.+)$/);
        if (match) {
            fromDefs.push({ word: match[1], meaning: match[2], fromTutorDef: true });
        }
    }
    const defined = uniqueClues(fromDefs);
    if (defined.length >= 4) {
        return defined.slice(0, maxWords);
    }
    const found = [...text.matchAll(/\b[A-Za-z]{4,12}\b/g)].map((match) => ({
        word: match[0],
        meaning: "",
        fromTutorDef: false,
    }));
    return uniqueClues(found).slice(0, maxWords);
}

function cellKey(row, col) {
    return `${row},${col}`;
}

function occupiedMap(placements) {
    const map = new Map();
    for (const p of placements) {
        for (let i = 0; i < p.word.length; i += 1) {
            const row = p.dir === "across" ? p.row : p.row + i;
            const col = p.dir === "across" ? p.col + i : p.col;
            map.set(cellKey(row, col), { letter: p.word[i], dir: p.dir });
        }
    }
    return map;
}

function inBounds(row, col, wordLen, dir) {
    if (row < 0 || col < 0 || row >= TUTOR_GRID_SIZE || col >= TUTOR_GRID_SIZE) {
        return false;
    }
    if (dir === "across") {
        return col + wordLen <= TUTOR_GRID_SIZE;
    }
    return row + wordLen <= TUTOR_GRID_SIZE;
}

function canPlace(word, row, col, dir, occupied) {
    if (!inBounds(row, col, word.length, dir)) {
        return false;
    }
    for (let i = 0; i < word.length; i += 1) {
        const r = dir === "across" ? row : row + i;
        const c = dir === "across" ? col : col + i;
        const existing = occupied.get(cellKey(r, c));
        if (existing && existing.letter !== word[i]) {
            return false;
        }
        if (!existing) {
            if (dir === "across") {
                if (occupied.has(cellKey(r - 1, c)) || occupied.has(cellKey(r + 1, c))) {
                    return false;
                }
            } else if (occupied.has(cellKey(r, c - 1)) || occupied.has(cellKey(r, c + 1))) {
                return false;
            }
        }
    }
    const before = dir === "across" ? cellKey(row, col - 1) : cellKey(row - 1, col);
    const after = dir === "across"
        ? cellKey(row, col + word.length)
        : cellKey(row + word.length, col);
    if (occupied.has(before) || occupied.has(after)) {
        return false;
    }
    return true;
}

function tryPlaceWord(word, occupied) {
    if (occupied.size === 0) {
        const col = Math.max(0, Math.floor((TUTOR_GRID_SIZE - word.length) / 2));
        const row = Math.floor((TUTOR_GRID_SIZE - 1) / 2);
        return { word, row, col, dir: "across" };
    }
    for (let i = 0; i < word.length; i += 1) {
        for (const [key, cell] of occupied.entries()) {
            if (cell.letter !== word[i]) {
                continue;
            }
            const [cr, cc] = key.split(",").map(Number);
            const dir = cell.dir === "across" ? "down" : "across";
            const row = dir === "across" ? cr : cr - i;
            const col = dir === "across" ? cc - i : cc;
            if (canPlace(word, row, col, dir, occupied)) {
                return { word, row, col, dir };
            }
        }
    }
    return null;
}

function numberPlacements(placements) {
    const starts = placements
        .map((p) => ({ ...p, startRow: p.row, startCol: p.col }))
        .sort((a, b) => a.startRow - b.startRow || a.startCol - b.startCol);
    const numbers = new Map();
    let next = 1;
    return starts.map((p) => {
        const key = cellKey(p.row, p.col);
        if (!numbers.has(key)) {
            numbers.set(key, next);
            next += 1;
        }
        return { ...p, number: numbers.get(key) };
    });
}

function toGrid(placements) {
    const rows = TUTOR_GRID_SIZE;
    const cols = TUTOR_GRID_SIZE;
    const grid = Array.from({ length: rows }, () => Array.from({ length: cols }, () => null));
    for (const p of placements) {
        for (let i = 0; i < p.word.length; i += 1) {
            const r = p.dir === "across" ? p.row : p.row + i;
            const c = p.dir === "across" ? p.col + i : p.col;
            if (r < 0 || c < 0 || r >= rows || c >= cols) {
                continue;
            }
            const prev = grid[r][c] || { letter: "", number: null };
            grid[r][c] = {
                letter: p.word[i],
                number: i === 0 ? p.number : prev.number,
            };
        }
    }
    for (const p of placements) {
        if (p.row < 0 || p.col < 0 || p.row >= rows || p.col >= cols) {
            continue;
        }
        const cell = grid[p.row][p.col];
        if (cell) {
            cell.number = p.number;
        }
    }
    return { grid, rows, cols, placements };
}

function withClueLists(clues, numbered, built) {
    return {
        clues,
        error: null,
        ...built,
        across: built.placements.filter((p) => p.dir === "across").sort((a, b) => a.number - b.number),
        down: built.placements.filter((p) => p.dir === "down").sort((a, b) => a.number - b.number),
        answers: numbered
            .slice()
            .sort((a, b) => a.number - b.number)
            .map((p) => ({
                word: p.word,
                meaning: p.meaning,
                number: p.number,
                fromTutorDef: Boolean(p.fromTutorDef),
            })),
    };
}

export function buildTutorCrossword(sourceText) {
    const clues = extractTutorClues(sourceText);
    if (clues.length < 4) {
        return { clues: [], placements: [], grid: [], error: "need-words" };
    }
    const placed = [];
    for (const clue of [...clues].sort((a, b) => b.word.length - a.word.length)) {
        const occupied = occupiedMap(placed);
        const next = tryPlaceWord(clue.word, occupied);
        if (next) {
            placed.push({
                ...next,
                meaning: clue.meaning,
                fromTutorDef: Boolean(clue.fromTutorDef),
            });
        }
    }
    if (placed.length < 4) {
        return { clues, placements: [], grid: [], error: "need-words" };
    }
    const numbered = numberPlacements(placed);
    const built = toGrid(numbered);
    return withClueLists(clues, numbered, built);
}

export function applyMeaningsToPuzzle(puzzle, meaningByWord) {
    if (!puzzle?.placements?.length) {
        return puzzle;
    }
    const lookup = (item) => {
        if (item.fromTutorDef && item.meaning) {
            return item.meaning;
        }
        const dic = meaningByWord.get(String(item.word || "").toLowerCase());
        if (dic) {
            return dic;
        }
        return item.meaning || "";
    };
    const placements = puzzle.placements.map((p) => ({ ...p, meaning: lookup(p) }));
    const patchList = (items) => (items || []).map((item) => ({ ...item, meaning: lookup(item) }));
    return {
        ...puzzle,
        placements,
        across: patchList(puzzle.across),
        down: patchList(puzzle.down),
        answers: patchList(puzzle.answers),
    };
}
